import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Loader2, Package, Ticket, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  fetchMyProducts, fetchProductForEdit, saveBundle,
  type CreatorProduct, type BundleConfig,
} from '@/services/creatorApi';

// Bundles and season passes. A bundle is a set of the creator's own listings sold
// at one price; a season pass is every featured event they run through a date.
// The server decides what's eligible and re-checks all of it on save.

const imgSrc = (k?: string | null) => !k ? null : (k.startsWith('http') || k.startsWith('/api/') ? k : `/api/s3/${k}`);
const money = (c: number) => `$${(c / 100).toFixed(c % 100 ? 2 : 0)}`;
const MAX_ITEMS = 12;

const typeLabel = (p: CreatorProduct) =>
  p.productType === 'webinar' ? 'Featured event'
  : p.productType === 'series' ? 'Course'
  : p.format === 'series' ? 'Series'
  : 'Video';

const isPickSession = (p: CreatorProduct) =>
  p.productType === 'webinar' && !p.attendAllDates && (p.eventDates?.length || 0) > 1;

const eventOver = (p: CreatorProduct) => {
  if (p.productType !== 'webinar') return false;
  const dates = (p.eventDates?.length ? p.eventDates : [p.eventDate]).filter(Boolean).map(d => new Date(d as string).getTime());
  if (!dates.length) return false;
  const hasReplay = (p.videos || []).some(v => v && (v.muxPlaybackId || v.s3Key));
  return Math.max(...dates) + ((p.durationMins || 120) + 60) * 60000 < Date.now() && !hasReplay;
};

const defaultTier = (p: CreatorProduct) => {
  const tiers = p.ticketTiers || [];
  return tiers.find(t => !t.membersOnly && !t.nonMembersOnly && !((t as { installments?: number }).installments! > 1)) || tiers[0];
};

const BundleBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId?: string }>();
  const { toast } = useToast();
  const editing = !!productId && productId !== 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<CreatorProduct[]>([]);
  const [mode, setMode] = useState<'fixed' | 'pass'>('fixed');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceText, setPriceText] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [tiers, setTiers] = useState<Record<string, string>>({});
  const [through, setThrough] = useState('');
  const [published, setPublished] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const mine = await fetchMyProducts();
        setProducts(mine.filter(p => p.productType !== 'bundle'));
        if (editing) {
          const b = await fetchProductForEdit(productId!);
          const cfg = (b.bundle || { mode: 'fixed' }) as BundleConfig;
          setMode(cfg.mode === 'pass' ? 'pass' : 'fixed');
          setTitle(b.title || '');
          setDescription(b.description || '');
          setPriceText(b.price ? String(b.price / 100) : '');
          setPicked(b.includedProductIds || []);
          setTiers(cfg.tiers || {});
          if (cfg.through) {
            // Stored as the end of that day in Pacific time; show the Pacific date.
            setThrough(new Date(cfg.through).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' }));
          }
          setPublished(!!b.published);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load your listings');
      } finally {
        setLoading(false);
      }
    })();
  }, [editing, productId]);

  // Listings that can go in a fixed bundle: live, one-time, not interview-only or
  // agency-only, and (for events) not over and not pick-a-session.
  const eligible = useMemo(() => products.filter(p =>
    p.published && p.pricingMode !== 'subscription' && !p.inquiry?.enabled
    && !(p.restrictToAgencies?.length) && !isPickSession(p) && !eventOver(p)
  ), [products]);

  const unitPrice = (p: CreatorProduct) => {
    const tiersList = p.ticketTiers || [];
    if (!tiersList.length) return p.price || 0;
    const t = tiersList.find(x => x.key === tiers[p._id]) || defaultTier(p);
    return t?.priceCents || 0;
  };

  // What the pass covers today: live events with a session on or before the end date.
  const passEvents = useMemo(() => {
    if (!through) return [];
    const end = new Date(`${through}T23:59:59-08:00`).getTime();
    const now = Date.now();
    return products.filter(p => p.productType === 'webinar' && p.published && p.pricingMode !== 'subscription'
      && !p.inquiry?.enabled && !(p.restrictToAgencies?.length)
      && (p.eventDates?.length ? p.eventDates : [p.eventDate]).filter(Boolean)
        .some(d => { const t = new Date(d as string).getTime(); return t <= end && now <= t + (p.durationMins || 120) * 60000; }));
  }, [products, through]);

  const separateCents = mode === 'fixed'
    ? picked.map(id => products.find(p => p._id === id)).filter(Boolean).reduce((s, p) => s + unitPrice(p as CreatorProduct), 0)
    : passEvents.reduce((s, p) => s + unitPrice(p), 0);
  const priceCents = Math.round(parseFloat(priceText || '0') * 100) || 0;
  const saveCents = Math.max(0, separateCents - priceCents);

  const toggle = (id: string) => setPicked(cur => cur.includes(id) ? cur.filter(x => x !== id) : (cur.length >= MAX_ITEMS ? cur : [...cur, id]));

  const save = async (publish: boolean) => {
    setError(null);
    if (!title.trim()) { setError('Give the bundle a title.'); return; }
    if (priceCents < 100) { setError('Set a price of at least $1.'); return; }
    if (mode === 'fixed' && picked.length < 2) { setError('Pick at least two listings.'); return; }
    if (mode === 'pass' && !through) { setError('Pick the last day the pass covers.'); return; }
    setSaving(true);
    try {
      const { product } = await saveBundle({
        title: title.trim(), description, price: priceCents, mode,
        includedProductIds: mode === 'fixed' ? picked : [],
        tiers: mode === 'fixed' ? Object.fromEntries(picked.filter(id => tiers[id]).map(id => [id, tiers[id]])) : {},
        through: mode === 'pass' ? through : undefined,
        published: publish,
      }, editing ? productId : undefined);
      setPublished(!!product.published);
      toast({ title: publish ? 'Bundle is live' : 'Draft saved', description: publish ? 'It shows on your store now.' : undefined });
      if (!editing) navigate(`/my-store/bundle/${product._id}`, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the bundle');
    } finally {
      setSaving(false);
    }
  };

  const card: React.CSSProperties = { background: 'var(--hj-surface)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', padding: 18 };
  const label: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--hj-ink)', marginBottom: 6 };
  const input: React.CSSProperties = { width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--hj-hairline)', background: 'var(--hj-bg)', color: 'var(--hj-ink)', fontSize: 15, fontFamily: 'inherit' };
  const modeBtn = (active: boolean): React.CSSProperties => ({
    flex: 1, minWidth: 0, textAlign: 'left', padding: 14, borderRadius: 'var(--hj-r-md)', cursor: 'pointer', fontFamily: 'inherit',
    background: active ? 'var(--hj-beige)' : 'var(--hj-surface)', color: active ? 'var(--hj-beige-ink)' : 'var(--hj-ink)',
    border: active ? '2px solid var(--hj-ink)' : '1px solid var(--hj-hairline)',
  });

  return (
    <main className="min-h-screen" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <div className="sticky top-0 z-10" style={{ background: 'var(--hj-bg)', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
        <div className="mx-auto flex items-center gap-3" style={{ maxWidth: 1120, padding: '14px 16px' }}>
          <button
            type="button"
            onClick={() => navigate('/my-store')}
            aria-label="Back to My Store"
            className="flex-shrink-0 inline-flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>My Store · {editing ? 'Edit bundle' : 'New bundle'}</div>
        </div>
      </div>

      <div className="mx-auto" style={{ maxWidth: 680, padding: '28px 16px 120px' }}>
        <div data-paper="beige" style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 'var(--hj-r-md)', padding: '20px 18px' }}>
          <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(24px, 4.4vw, 34px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {editing ? title || 'Your bundle' : 'Sell more of what you already have'}
          </h1>
          <p className="m-0" style={{ marginTop: 8, fontSize: 14.5, lineHeight: 1.55 }}>
            Put a few listings together at one price, or sell a season pass to every event you run through a date you choose.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center" style={{ padding: 48 }}><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            <div className="flex gap-3" style={{ marginTop: 18, flexWrap: 'wrap' }}>
              <button type="button" style={modeBtn(mode === 'fixed')} onClick={() => setMode('fixed')} aria-pressed={mode === 'fixed'}>
                <div className="flex items-center gap-2" style={{ fontWeight: 800, fontSize: 15 }}><Package className="w-4 h-4" /> Bundle</div>
                <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>A course plus two sessions, say. You pick what's in it.</div>
              </button>
              <button type="button" style={modeBtn(mode === 'pass')} onClick={() => setMode('pass')} aria-pressed={mode === 'pass'}>
                <div className="flex items-center gap-2" style={{ fontWeight: 800, fontSize: 15 }}><Ticket className="w-4 h-4" /> Season pass</div>
                <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>Every featured event you run through an end date, including ones you add later.</div>
              </button>
            </div>

            <div style={{ ...card, marginTop: 18 }}>
              <label style={label} htmlFor="b-title">Title</label>
              <input id="b-title" style={input} value={title} maxLength={120} onChange={e => setTitle(e.target.value)}
                placeholder={mode === 'pass' ? 'Fall season pass' : 'The audition starter pack'} />
              <label style={{ ...label, marginTop: 16 }} htmlFor="b-desc">What they get</label>
              <textarea id="b-desc" style={{ ...input, minHeight: 96, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)}
                placeholder="One or two sentences on who this is for." />
            </div>

            {mode === 'fixed' ? (
              <div style={{ ...card, marginTop: 14 }}>
                <div style={label}>What's in it <span style={{ fontWeight: 500, color: 'var(--hj-ink-2)' }}>({picked.length} of up to {MAX_ITEMS})</span></div>
                {eligible.length === 0 ? (
                  <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-ink-2)' }}>Publish at least two listings first, then come back to bundle them.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {eligible.map(p => {
                      const on = picked.includes(p._id);
                      const src = imgSrc(p.coverImage);
                      return (
                        <div key={p._id} style={{ border: on ? '2px solid var(--hj-ink)' : '1px solid var(--hj-hairline)', borderRadius: 12, padding: 10 }}>
                          <button type="button" onClick={() => toggle(p._id)} aria-pressed={on}
                            className="flex items-center gap-3 w-full text-left"
                            style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', color: 'var(--hj-ink)', fontFamily: 'inherit', minHeight: 44 }}>
                            <span className="flex-shrink-0 inline-flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: 6, border: '1.5px solid var(--hj-ink)', background: on ? 'var(--hj-ink)' : 'transparent', color: 'var(--hj-bg)' }}>
                              {on && <Check className="w-4 h-4" />}
                            </span>
                            {src ? <img src={src} alt="" style={{ width: 52, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} /> : null}
                            <span className="flex-1 min-w-0">
                              <span className="block" style={{ fontSize: 14.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                              <span className="block" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)' }}>
                                {typeLabel(p)}{p.eventDate && p.productType === 'webinar' ? ` · ${new Date(p.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''} · {money(unitPrice(p))}
                              </span>
                            </span>
                          </button>
                          {on && (p.ticketTiers?.length || 0) > 1 && (
                            <div style={{ marginTop: 8, paddingLeft: 36 }}>
                              <label style={{ fontSize: 12.5, fontWeight: 700 }} htmlFor={`tier-${p._id}`}>Seat in the bundle</label>
                              <select id={`tier-${p._id}`} style={{ ...input, padding: '8px 10px', fontSize: 14, marginTop: 4 }}
                                value={tiers[p._id] || defaultTier(p)?.key || ''}
                                onChange={e => setTiers(t => ({ ...t, [p._id]: e.target.value }))}>
                                {(p.ticketTiers || []).filter(t => !t.membersOnly && !t.nonMembersOnly).map(t => (
                                  <option key={t.key} value={t.key}>{t.label} ({money(t.priceCents)})</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="m-0" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                  Events with several dates to choose from can't go in a bundle. A season pass covers every date.
                </p>
              </div>
            ) : (
              <div style={{ ...card, marginTop: 14 }}>
                <label style={label} htmlFor="b-through">Covers events through</label>
                <input id="b-through" type="date" style={input} value={through} onChange={e => setThrough(e.target.value)}
                  min={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' })} />
                {through && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700 }}>{passEvents.length} event{passEvents.length === 1 ? '' : 's'} on the calendar now</div>
                    <ul className="m-0" style={{ paddingLeft: 18, marginTop: 6, fontSize: 13.5, lineHeight: 1.6 }}>
                      {passEvents.map(p => <li key={p._id}>{p.title} · {money(unitPrice(p))}</li>)}
                    </ul>
                    <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.5 }}>
                      Events you publish later that fall on or before this date are added for everyone who holds the pass.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div style={{ ...card, marginTop: 14 }}>
              <label style={label} htmlFor="b-price">Price</label>
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 18, fontWeight: 700 }}>$</span>
                <input id="b-price" inputMode="decimal" style={{ ...input, maxWidth: 160 }} value={priceText}
                  onChange={e => setPriceText(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="99" />
              </div>
              {separateCents > 0 && (
                <p className="m-0" style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5 }}>
                  Bought one by one: <strong>{money(separateCents)}</strong>
                  {priceCents > 0 && (saveCents > 0
                    ? <> · buyers save <strong>{money(saveCents)}</strong></>
                    : <> · set the price below that so buyers save something</>)}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="m-0" style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'rgb(220 38 38 / 0.1)', color: 'rgb(185 28 28)', fontSize: 14, fontWeight: 600 }}>{error}</p>
            )}

            <div className="flex gap-3" style={{ marginTop: 18, flexWrap: 'wrap' }}>
              <button type="button" disabled={saving} onClick={() => save(true)}
                className="inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ flex: '1 1 160px', minHeight: 48, borderRadius: 999, background: 'var(--hj-ink)', color: 'var(--hj-bg)', border: 0, fontWeight: 800, fontSize: 15, fontFamily: 'inherit', cursor: 'pointer' }}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}{published ? 'Save' : 'Publish'}
              </button>
              <button type="button" disabled={saving} onClick={() => save(false)}
                className="inline-flex items-center justify-center disabled:opacity-50"
                style={{ flex: '1 1 160px', minHeight: 48, borderRadius: 999, background: 'transparent', color: 'var(--hj-ink)', border: '1px solid var(--hj-hairline)', fontWeight: 700, fontSize: 15, fontFamily: 'inherit', cursor: 'pointer' }}>
                {published ? 'Unpublish' : 'Save draft'}
              </button>
            </div>
            {editing && (
              <button type="button" onClick={() => navigate(`/course/${productId}`)}
                style={{ marginTop: 14, background: 'transparent', border: 0, color: 'var(--hj-ink)', textDecoration: 'underline', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 }}>
                View the bundle page
              </button>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default BundleBuilderPage;
